import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import { useAccountId } from "../../../auth/useAccountId";
import useHasAnyOfScopes from "../../../common/hooks/useHasAnyOfScopes";
import { CORE_API_HOSTNAME } from "../../../env";
import fulfill from "../../../fulfill";
import type { BillingPayoutRule } from "../../../types/billing";
type Response = {
    payout_rules: BillingPayoutRule[];
    starting_after: string | undefined;
};

const fetchPayoutRulesReq = async (
    accountId: string,
    params: URLSearchParams,
): Promise<Response> => {
    return fulfill.get({
        accountId,
        url: `${CORE_API_HOSTNAME}/v1/accounts/${accountId}/billing/payout-rules?${params.toString()}`,
        handlers: {
            200: (response: Response) => response,
            404: () => [],
            400: () => [],
        },
    });
};

const recursivelyFetchPayoutRules = async (
    accountId: string,
    params: URLSearchParams,
    rules: BillingPayoutRule[],
): Promise<BillingPayoutRule[]> => {
    const { payout_rules, starting_after } = await fetchPayoutRulesReq(
        accountId,
        params,
    );
    const nextRules = [...rules, ...payout_rules];
    if (
        payout_rules.length.toString() === params.get("limit") &&
        starting_after
    ) {
        const nextParams = new URLSearchParams(params);
        nextParams.delete("starting_after");
        nextParams.append("starting_after", starting_after);
        return recursivelyFetchPayoutRules(accountId, params, nextRules);
    }
    return nextRules;
};

const fetchPayoutRules = async (key: string): Promise<BillingPayoutRule[]> => {
    const [accountId, rest] = key.split("/");
    const [, search] = rest.split("?");
    const params = new URLSearchParams(search);
    return await recursivelyFetchPayoutRules(accountId, params, []);
};

const fetchPostPayoutRule = async (
    _key: string,
    options: {
        arg: {
            accountId: string;
            newPayoutRule: BillingPayoutRule;
        };
    },
): Promise<BillingPayoutRule> => {
    const { accountId, newPayoutRule } = options.arg;
    return fulfill.post({
        accountId,
        url: `${CORE_API_HOSTNAME}/v1/accounts/${accountId}/billing/payout-rules?include_deleted=true`,
        json: newPayoutRule,
        handlers: {
            200: (response: BillingPayoutRule) => response,
            404: () => null,
        },
    });
};

const defaultParams = new URLSearchParams({
    limit: "100",
    include_deleted: "true",
});

const mergeSearchParams = (...params: URLSearchParams[]): URLSearchParams => {
    const merged = new URLSearchParams();
    params.forEach((param) => {
        for (const [key, value] of param.entries()) {
            merged.append(key, value);
        }
    });
    return merged;
};

export const usePayoutRules = (initialParams = defaultParams) => {
    const accountId = useAccountId();
    const canReadBilling = useHasAnyOfScopes(["admin:billing", "read:billing"]);
    const canWriteBilling = useHasAnyOfScopes([
        "admin:billing",
        "write:billing",
    ]);
    const params = mergeSearchParams(defaultParams, initialParams);
    const key = `${accountId}/payout-rules?${params?.toString()}`;
    const {
        data: payoutRules,
        error,
        isLoading,
    } = useSWR(() => (canReadBilling ? key : null), fetchPayoutRules);
    const {
        trigger: createPayoutRule,
        isMutating: isCreatingPayoutRule,
        error: createPayoutRuleError,
        data: createdPayoutRule,
    } = useSWRMutation(
        () => (canWriteBilling ? key : null),
        fetchPostPayoutRule,
    );

    return {
        payoutRules: (payoutRules || []).sort((a, b) => {
            if (
                (a.updated_at || a.created_at || "") <
                (b.updated_at || b.created_at || "")
            ) {
                return 1;
            }
            if (
                (a.updated_at || a.created_at || "") >
                (b.updated_at || b.created_at || "")
            ) {
                return -1;
            }
            return 0;
        }),
        error,
        isLoading,
        createPayoutRule,
        isCreatingPayoutRule,
        createPayoutRuleError,
        createdPayoutRule,
    };
};
